import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as tareasApi from '@/api/tareas-cliente.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { PageHeader } from '@/components/common/PageHeader';
import { Cargando } from '@/components/common/Cargando';
import { useToast } from '@/hooks/useToast';
import { dateTime } from '@/lib/format';
import { armarAprobacionDeZonas, trabajosQueCambian } from '@/lib/tareas-cliente';

/**
 * "ORDENAR LAS ZONAS".
 *
 * En los trabajos viejos la zona se escribía a mano, y el mismo lugar quedó
 * escrito de varias formas. Acá se ve cada grupo con sus formas y cuántos
 * trabajos tiene; el dueño elige a qué zona va cada uno y aprueba.
 *
 * Nada cambia hasta que aprueba. El destino puede ser otra zona del catálogo
 * (por ejemplo, "Naco" dentro de "Santo Domingo") o un nombre nuevo, que entra
 * al catálogo para que los trabajos nuevos lo puedan elegir.
 */
export default function Zonas() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['zonas', 'agrupacion'],
    queryFn: tareasApi.propuestaDeZonas,
  });
  const [destinos, setDestinos] = useState<Record<string, string>>({});

  const porRevisar = useMemo(() => (data?.grupos ?? []).filter((g) => g.porRevisar), [data]);

  // La propuesta llega como valor inicial de cada destino; el dueño la cambia.
  useEffect(() => {
    if (!data) return;
    setDestinos(Object.fromEntries(data.grupos.map((g) => [g.clave, g.propuesta])));
  }, [data]);

  const aprobar = useMutation({
    mutationFn: () => tareasApi.aprobarZonas(armarAprobacionDeZonas(porRevisar, destinos)),
    onSuccess: (r) => {
      toast.success(
        `Listo: ${r.trabajosCambiados} trabajo${r.trabajosCambiados === 1 ? '' : 's'} cambiaron de zona`,
      );
      void qc.invalidateQueries({ queryKey: ['zonas'] });
      void qc.invalidateQueries({ queryKey: ['tareas-cliente'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !data) return <Cargando />;

  const cambian = trabajosQueCambian(porRevisar, destinos);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        title="Ordenar las zonas"
        description="La misma zona quedó escrita de varias formas en los trabajos viejos. Elige a qué zona va cada grupo y aprueba: los reportes la van a contar como una sola."
      />

      {data.aprobadaEl && (
        <p className="rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-300">
          Ya se aprobó una agrupación el {dateTime(data.aprobadaEl)}. Si aparecen formas nuevas, se
          pueden ordenar otra vez.
        </p>
      )}

      {porRevisar.length === 0 ? (
        <Card>
          <CardBody className="space-y-2">
            <p className="text-sm text-slate-700 dark:text-slate-200">
              No hay zonas para ordenar: cada zona está escrita de una sola forma y está en el
              catálogo.
            </p>
            <Link to="/tareas" className="text-sm font-medium text-brand-600 hover:text-brand-700">
              Volver a lo que falta configurar
            </Link>
          </CardBody>
        </Card>
      ) : (
        <>
          <datalist id="zonas-catalogo">
            {data.catalogo.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>

          <div className="grid gap-4 md:grid-cols-2">
            {porRevisar.map((g, i) => (
              <Card key={g.clave}>
                <CardBody className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {g.trabajos} trabajo{g.trabajos === 1 ? '' : 's'}
                    </p>
                    {!g.enCatalogo && <Badge variant="warning">No está en el catálogo</Badge>}
                  </div>
                  <ul className="space-y-1">
                    {g.variantes.map((v) => (
                      <li key={v.zona} className="flex justify-between gap-2 text-sm">
                        <span className="truncate text-slate-600 dark:text-slate-300">
                          «{v.zona}»
                        </span>
                        <span className="shrink-0 tabular-nums text-slate-400">{v.trabajos}</span>
                      </li>
                    ))}
                  </ul>
                  <div>
                    <label
                      htmlFor={`zona-destino-${i}`}
                      className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                    >
                      Queda como
                    </label>
                    <input
                      id={`zona-destino-${i}`}
                      list="zonas-catalogo"
                      value={destinos[g.clave] ?? ''}
                      maxLength={200}
                      onChange={(e) => setDestinos((d) => ({ ...d, [g.clave]: e.target.value }))}
                      className="h-10 w-full rounded-md border border-surface-border bg-transparent px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700"
                    />
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>

          <Card>
            <CardBody className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Al aprobar, {cambian} trabajo{cambian === 1 ? '' : 's'} cambia
                {cambian === 1 ? '' : 'n'} de zona.
                {data.sinZona > 0 &&
                  ` Hay ${data.sinZona} trabajo${data.sinZona === 1 ? '' : 's'} sin zona: esos no se tocan.`}
              </p>
              <Button loading={aprobar.isPending} onClick={() => aprobar.mutate()}>
                Aprobar
              </Button>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
